import { useState } from 'react'
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom'
import { cn } from '@/utils'
import { LayoutDashboard, FileText, /* ShoppingBag, */ Users, CreditCard, Menu, LogOut, User, BookOpen, Library, Bot } from 'lucide-react'
import { usePermissions } from '@/hooks/usePermissions'

interface LayoutProps {
  children?: React.ReactNode
  showSidebar?: boolean
}

function Layout({ children, showSidebar = true }: LayoutProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout, isAdmin, isCreator, isGod, canManageUsers /* , canManageMarketplace */ } = usePermissions()
  const [sidebarOpen, setSidebarOpen] = useState(false)

  const navigation = [
    { name: 'Dashboard', href: '/', icon: LayoutDashboard, show: true },
    { name: 'Planos', href: '/plans', icon: CreditCard, show: isAdmin() || isGod() },
    { name: 'Usuários', href: '/users', icon: Users, show: canManageUsers() },
    { name: 'Conteúdo do Blog', href: '/content', icon: FileText, show: isCreator() || isAdmin() || isGod() },
    // { name: 'Marketplace', href: '/marketplace', icon: ShoppingBag, show: canManageMarketplace() },
    { name: 'Conteúdo da Biblioteca', href: '/library-docs', icon: Library, show: isAdmin() || isGod() },
    { name: 'Base de Conhecimento', href: '/knowledge', icon: BookOpen, show: isAdmin() || isGod() },
    { name: 'Modelos do Chat', href: '/chat-models', icon: Bot, show: isAdmin() || isGod() },
  ].filter(item => item.show)

  const currentPage = navigation.find(
    item => location.pathname === item.href || location.pathname.startsWith(`${item.href}/`)
  )

  const handleLogout = async () => {
    try {
      await logout()
      navigate('/login')
    } catch (error) {
      console.error('Erro ao fazer logout:', error)
    }
  }

  return (
    <div className="flex h-screen bg-gray-100">
      {showSidebar && (
        <>
          {/* Sidebar */}
          <div className={cn(
            'fixed inset-y-0 left-0 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out',
            'md:relative md:translate-x-0',
            sidebarOpen ? 'z-50' : 'z-40',
            !sidebarOpen && '-translate-x-full',
            sidebarOpen && 'translate-x-0'
          )}>
            <div className="p-6">
              <h1 className="text-2xl font-bold text-gray-900">RegenerAI</h1>
              <p className="text-sm text-gray-600">Admin Dashboard</p>
            </div>
            <nav className="mt-6">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    'flex items-center px-6 py-3 text-sm font-medium transition-colors',
                    location.pathname === item.href || location.pathname.startsWith(`${item.href}/`)
                      ? 'bg-primary text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  )}
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon className="mr-3 h-5 w-5" />
                  {item.name}
                </Link>
              ))}
            </nav>
          </div>

          {/* Mobile Sidebar Overlay */}
          {sidebarOpen && (
            <div
              className="md:hidden fixed inset-0 bg-black bg-opacity-50"
              style={{ zIndex: 45 }}
              onClick={() => setSidebarOpen(false)}
            />
          )}
        </>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                {/* Mobile Menu Button */}
                {showSidebar && (
                  <button
                    onClick={() => setSidebarOpen(!sidebarOpen)}
                    className="md:hidden p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                  >
                    <Menu className="h-6 w-6" />
                  </button>
                )}

                {/* Page Title */}
                <h2 className="text-lg font-semibold text-gray-900">
                  {currentPage?.name}
                </h2>
              </div>

              {/* Desktop User Info */}
              <div className="hidden md:flex items-center space-x-4">
                <div className="flex items-center space-x-2 text-sm">
                  <User className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-700 hidden lg:block">
                    {user?.name || 'Usuário'}
                  </span>
                  <span className="text-gray-500 hidden lg:block">
                    ({user?.role || 'guest'})
                  </span>
                </div>
                <button
                  onClick={handleLogout}
                  className="flex items-center space-x-1 p-2 text-gray-400 hover:text-red-600 transition-colors"
                  title="Sair"
                >
                  <LogOut className="h-4 w-4" />
                </button>
              </div>

              {/* Mobile User Info */}
              <div className="md:hidden flex items-center space-x-2">
                <span className="text-sm font-medium text-gray-700">
                  {user?.name?.split(' ')[0] || 'Usuário'}
                </span>
                <button
                  onClick={handleLogout}
                  className="p-2 text-gray-400 hover:text-red-600"
                >
                  <LogOut className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto p-6">
          {children || <Outlet />}
        </main>
      </div>
    </div>
  )
}

export default Layout